/**
 * Error Parsing Utilities
 * Extracts clean, user-friendly error messages from transaction errors
 */

/**
 * Extracts a clean, user-friendly error message from an error object
 * @param {Error|Object} error - The error object from a transaction
 * @returns {string} - A clean, understandable error message
 */
export function getCleanErrorMessage(error) {
    try {
        if (!error) {
            return 'An unknown error occurred';
        }

        // Check for user cancellation first
        if (error.code === 'ACTION_REJECTED' || 
        error.code === '4001' ||
        (typeof error.message === 'string' && (
            error.message.toLowerCase().includes('user rejected') ||
            error.message.toLowerCase().includes('user denied') ||
            error.message.toLowerCase().includes('user cancelled')
        )) ||
        (typeof error.reason === 'string' && (
            error.reason.toLowerCase().includes('user rejected') ||
            error.reason.toLowerCase().includes('user denied') ||
            error.reason.toLowerCase().includes('user cancelled')
        ))) {
            return 'Transaction was cancelled by user';
        }

        // Handle string errors
        if (typeof error === 'string') {
            return extractMessageFromString(error);
        }

        // Check for JavaScript runtime errors (technical errors that should be hidden from users)
        const errorMessage = error.message || error.toString() || '';
        if (typeof errorMessage === 'string') {
            const lowerMessage = errorMessage.toLowerCase();
            
            // Catch common JavaScript runtime errors and convert to user-friendly messages
            if (lowerMessage.includes('is not a function') ||
                lowerMessage.includes('cannot read property') ||
                lowerMessage.includes('cannot read properties') ||
                lowerMessage.includes('undefined is not an object') ||
                lowerMessage.includes('null is not an object') ||
                lowerMessage.includes('typeerror') ||
                lowerMessage.includes('referenceerror')) {
                // These are internal JavaScript errors - don't show technical details to users
                return 'An unexpected error occurred. Please try again.';
            }
        }

        // Try to get the reason first (most specific)
        if (error.reason) {
            return formatErrorMessage(error.reason);
        }

        // Try nested error object (common in ethers.js)
        if (error.error) {
            const nestedError = error.error;
            // Check for user cancellation in nested error
            if (nestedError.code === 'ACTION_REJECTED' || 
            nestedError.code === '4001' ||
            (typeof nestedError.message === 'string' && (
                nestedError.message.toLowerCase().includes('user rejected') ||
                nestedError.message.toLowerCase().includes('user denied') ||
                nestedError.message.toLowerCase().includes('user cancelled')
            )) ||
            (typeof nestedError.reason === 'string' && (
                nestedError.reason.toLowerCase().includes('user rejected') ||
                nestedError.reason.toLowerCase().includes('user denied') ||
                nestedError.reason.toLowerCase().includes('user cancelled')
                ))) {
                return 'Transaction was cancelled by user';
            }
            if (nestedError.reason) {
                return formatErrorMessage(nestedError.reason);
            }
            if (nestedError.message) {
                return extractMessageFromString(nestedError.message);
            }
            if (nestedError.data) {
                const dataMessage = extractMessageFromData(nestedError.data);
                if (dataMessage) return dataMessage;
            }
        }

        // Try error.data directly
        if (error.data) {
            const dataMessage = extractMessageFromData(error.data);
            if (dataMessage) return dataMessage;
        }

        // Try error.message
        if (error.message) {
            return extractMessageFromString(error.message);
        }

        // Fallback
        return 'Transaction failed. Please try again.';
    } catch (e) {
        // If any error occurs during error parsing, return a safe generic message
        // This prevents technical errors from being shown to users
        console.error('Error parsing error message:', e);
        return 'An unexpected error occurred. Please try again.';
    }
}

/**
 * Extracts a clean message from a string that may contain stack traces and hex data
 * @param {string} message - The error message string
 * @returns {string} - Clean error message
 */
function extractMessageFromString(message) {
    if (!message || typeof message !== 'string') {
        return 'An unknown error occurred';
    }

    // Check for JavaScript runtime errors first (before processing)
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('is not a function') ||
        lowerMessage.includes('cannot read property') ||
        lowerMessage.includes('cannot read properties') ||
        lowerMessage.includes('undefined is not an object') ||
        lowerMessage.includes('null is not an object') ||
        lowerMessage.includes('typeerror') ||
        lowerMessage.includes('referenceerror')) {
        // These are internal JavaScript errors - don't show technical details to users
        return 'An unexpected error occurred. Please try again.';
    }

    // Check for MetaMask-style RPC endpoint HTTP client errors and provide a guided fix
    if (lowerMessage.includes('rpc endpoint returned http client error')) {
        return 'Your wallet\'s RPC endpoint returned an HTTP error. If you are using Arbitrum Sepolia in MetaMask, please edit that network and set the RPC URL to https://sepolia-rollup.arbitrum.io/rpc, then try again.';
    }

    // Check for generic RPC/HTTP errors and provide clear retry messages
    if (lowerMessage.includes('http request failed') ||
        lowerMessage.includes('fetch failed') ||
        lowerMessage.includes('rpc connection error') ||
        lowerMessage.includes('rpc error') ||
        (lowerMessage.includes('failed to check gas balance') && (lowerMessage.includes('http') || lowerMessage.includes('rpc') || lowerMessage.includes('network')))) {
        return 'RPC connection error: Unable to connect to the network. This is likely a temporary issue. Please retry the operation.';
    }

    // Remove stack traces (lines starting with "    at" or containing file paths)
    // Also remove lines with technical metadata
    let cleanMessage = message.split('\n')
        .filter(line => {
            const trimmed = line.trim();
            // Filter out stack trace lines
            if (trimmed.startsWith('at ') || 
                trimmed.startsWith('Error:') && trimmed.includes('at ') ||
                trimmed.includes('chrome-extension://') ||
                trimmed.includes('common-') && trimmed.includes('.js') ||
                trimmed.includes('node_modules/') ||
                trimmed.includes('webpack://')) {
                return false;
            }
            // Filter out very long hex strings (likely error data)
            if (trimmed.length > 100 && /^0x[a-fA-F0-9]+$/.test(trimmed)) {
                return false;
            }
            // Filter out lines that are just technical metadata
            if (trimmed.match(/^(from|to|data|arguments|details|version|code|estimate gas):/i) && trimmed.includes('0x')) {
                return false;
            }
            return true;
        })
        .join(' ')
        .trim();

    // Extract "execution reverted: <message>" or "execution reverted with reason: <message>" pattern
    // This handles both ethers.js and viem error formats
    // First, try to extract the core error message before any technical details
    const executionRevertedMatch = cleanMessage.match(/execution reverted(?:\s+with reason)?[:\s]+(.+?)(?:\s*(?:\(|code|version|available|required|token|address|estimate|gas|arguments|details|software|from|to|data|viem|ethers|suggestion)|$)/i);
    if (executionRevertedMatch && executionRevertedMatch[1]) {
        let revertMessage = executionRevertedMatch[1].trim();
        
        // Handle ERC20 errors - extract the error message
        // Match "ERC20: <error>" pattern, stopping before technical metadata
        const erc20Match = revertMessage.match(/ERC20[:\s]+([^\.]+?)(?:\.|$|\s*(?:\(|code|version|available|required|token|address|estimate|gas|arguments|details|software|from|to|data|viem|ethers|suggestion))/i);
        if (erc20Match && erc20Match[1]) {
            const erc20Error = erc20Match[1].trim();
            // Map common ERC20 errors to user-friendly messages
            const erc20ErrorLower = erc20Error.toLowerCase();
            if (erc20ErrorLower.includes('transfer amount exceeds balance')) {
                return 'Insufficient token balance. You do not have enough tokens to complete this transaction.';
            }
            if (erc20ErrorLower.includes('transfer amount exceeds allowance')) {
                return 'Insufficient allowance. Please approve more tokens first.';
            }
            if (erc20ErrorLower.includes('insufficient balance')) {
                return 'Insufficient token balance.';
            }
            if (erc20ErrorLower.includes('insufficient allowance')) {
                return 'Insufficient allowance. Please approve more tokens first.';
            }
            // Return formatted ERC20 error if no specific mapping
            return formatErrorMessage(erc20Error);
        }
        
        // Handle TIP20 token errors - extract just the error type (e.g., "InsufficientBalance")
        const tip20Match = revertMessage.match(/TIP20\s+token\s+error[:\s]+([A-Za-z]+)/i);
        if (tip20Match && tip20Match[1]) {
            // Return just the error type, e.g., "InsufficientBalance" becomes "Insufficient balance"
            const errorType = tip20Match[1];
            // Convert camelCase to readable format
            const readableError = errorType.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
            return formatErrorMessage(readableError);
        }
        
        // Remove technical details that might appear after the error message
        // Stop at keywords that indicate technical metadata
        revertMessage = revertMessage.split(/\s*(?:available|required|token|address|estimate|gas|arguments|details|software|version|from|to|data|viem|ethers)/i)[0].trim();
        
        // Remove trailing metadata and hex data
        revertMessage = revertMessage.replace(/0x[a-fA-F0-9]{40,}/g, '').trim();
        revertMessage = revertMessage.split(/[(\s]+code|version|UNPREDICTABLE/i)[0].trim();
        
        if (revertMessage) {
            return formatErrorMessage(revertMessage);
        }
    }

    // Extract error code patterns and provide friendly messages
    if (cleanMessage.includes('UNPREDICTABLE_GAS_LIMIT')) {
        // Try to extract the underlying revert reason
        const revertMatch = cleanMessage.match(/revert[ed]?[:\s]+(.+?)(?:\s|$|\(|code|version)/i);
        if (revertMatch && revertMatch[1]) {
            return formatErrorMessage(revertMatch[1].trim());
        }
        return 'Transaction would fail. Please check your inputs and try again.';
    }

    // Remove JSON-like structures and hex data more aggressively
    cleanMessage = cleanMessage.replace(/\{[^}]*"code"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"message"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"data"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"from"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"to"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"arguments"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"details"[^}]*\}/g, '');
    
    // Remove all hex strings (addresses, data, etc.) - be aggressive
    cleanMessage = cleanMessage.replace(/0x[a-fA-F0-9]{20,}/g, '');
    
    // Remove technical metadata patterns
    cleanMessage = cleanMessage.replace(/code=UNPREDICTABLE_GAS_LIMIT[^,)]*/gi, '');
    cleanMessage = cleanMessage.replace(/version=[^,)]*/gi, '');
    cleanMessage = cleanMessage.replace(/suggestion[:\s]+[^\s,)]+/gi, '');
    
    // Remove detailed error information (available amount, required amount, token address, etc.)
    cleanMessage = cleanMessage.replace(/\b(available|required)\s+(amount|balance)[:\s]+\d+/gi, '');
    cleanMessage = cleanMessage.replace(/\btoken\s+address[:\s]+0x[a-fA-F0-9]{40}/gi, '');
    cleanMessage = cleanMessage.replace(/\bestimate\s+gas\s+arguments[:\s]*\{[^}]*\}/gi, '');
    cleanMessage = cleanMessage.replace(/\bdetails[:\s]*\{[^}]*\}/gi, '');
    cleanMessage = cleanMessage.replace(/\bsoftware\s+version[:\s]+[^\s,)]+/gi, '');
    
    // Remove viem/ethers specific metadata
    cleanMessage = cleanMessage.replace(/\bfrom[:\s]+0x[a-fA-F0-9]{40}/gi, '');
    cleanMessage = cleanMessage.replace(/\bto[:\s]+0x[a-fA-F0-9]{40}/gi, '');
    cleanMessage = cleanMessage.replace(/\bdata[:\s]+0x[a-fA-F0-9]+/gi, '');
    cleanMessage = cleanMessage.replace(/\barguments[:\s]*\{[^}]*\}/gi, '');
    cleanMessage = cleanMessage.replace(/\bviem@[\d.]+/gi, '');
    cleanMessage = cleanMessage.replace(/\bethers@[\d.]+/gi, '');
    
    // Remove lines that are just technical metadata (if message was multi-line)
    cleanMessage = cleanMessage.split('\n')
        .filter(line => {
            const trimmed = line.trim();
            // Remove lines that are just metadata
            if (trimmed.match(/^(from|to|data|arguments|details|version|code|suggestion|estimate gas):/i)) {
                return false;
            }
            return true;
        })
        .join(' ')
        .trim();

    // Remove common error metadata
    cleanMessage = cleanMessage.replace(/\(code[^)]*\)/gi, '');
    cleanMessage = cleanMessage.replace(/\(version[^)]*\)/gi, '');

    // Clean up multiple spaces
    cleanMessage = cleanMessage.replace(/\s+/g, ' ').trim();

    // If message is still too long or contains technical details, try to extract the meaningful part
    if (cleanMessage.length > 200) {
        // Try to find the first sentence or meaningful phrase
        const sentences = cleanMessage.split(/[.!?]/);
        if (sentences[0] && sentences[0].length < 200) {
            cleanMessage = sentences[0].trim();
        } else {
            // Take first 150 chars and add ellipsis
            cleanMessage = cleanMessage.substring(0, 150).trim() + '...';
        }
    }

    return formatErrorMessage(cleanMessage || 'Transaction failed. Please try again.');
}

/**
 * Extracts error message from error data (hex encoded)
 * @param {string} data - Error data (usually hex encoded)
 * @returns {string|null} - Extracted message or null
 */
function extractMessageFromData(data) {
    if (!data || typeof data !== 'string') {
        return null;
    }

    // If it's a hex string starting with 0x, try to decode common error selectors
    if (data.startsWith('0x')) {
        // Common error selectors (first 4 bytes)
        const errorSelectors = {
            '0x08c379a0': 'Error(string)', // Standard Solidity error
            '0x4e487b71': 'Panic(uint256)', // Solidity panic
        };

        const selector = data.substring(0, 10);
        if (errorSelectors[selector]) {
            // Try to extract the string message (after the selector)
            try {
                // Error(string) encoding: selector (4 bytes) + offset (32 bytes) + length (32 bytes) + string data
                if (selector === '0x08c379a0' && data.length > 138) {
                    // This is a simplified extraction - full ABI decoding would be more accurate
                    return 'Transaction reverted with an error message';
                }
            } catch (e) {
                // Ignore decoding errors
            }
        }
    }

    return null;
}

/**
 * Formats error messages to be more user-friendly
 * @param {string} message - Raw error message
 * @returns {string} - Formatted error message
 */
function formatErrorMessage(message) {
    if (!message) {
        return 'Transaction failed. Please try again.';
    }

    // Ensure message is a string
    if (typeof message !== 'string') {
        // Try to convert to string
        try {
            message = String(message);
        } catch (e) {
            return 'Transaction failed. Please try again.';
        }
    }

    // Common error message mappings
    const errorMappings = {
        'self-transfer': 'Cannot transfer to yourself',
        'self transfer': 'Cannot transfer to yourself',
        'insufficient funds': 'Insufficient funds for this transaction',
        'insufficient balance': 'Insufficient balance',
        'insufficientbalance': 'Insufficient balance',
        'op_no_trust': 'Missing trustline for this asset. Add the required trustline in your wallet, then try again.',
        'no trustline': 'Missing trustline for this asset. Add the required trustline in your wallet, then try again.',
        'trustline': 'This operation requires a trustline. Add the trustline and try again.',
        'op_underfunded': 'Insufficient balance to complete this operation.',
        'underfunded': 'Insufficient balance to complete this operation.',
        'op_low_reserve': 'Insufficient XLM reserve. Add more XLM to your account and try again.',
        'low reserve': 'Insufficient XLM reserve. Add more XLM to your account and try again.',
        'transfer amount exceeds balance': 'Insufficient token balance. You do not have enough tokens to complete this transaction.',
        'transfer amount exceeds allowance': 'Insufficient allowance. Please approve more tokens first.',
        'erc20: transfer amount exceeds balance': 'Insufficient token balance. You do not have enough tokens to complete this transaction.',
        'erc20: transfer amount exceeds allowance': 'Insufficient allowance. Please approve more tokens first.',
        'user denied': 'Transaction was cancelled by user',
        'user rejected': 'Transaction was cancelled by user',
        'user rejected the request': 'Transaction was cancelled by user',
        'transaction underpriced': 'Gas price too low. Please try again.',
        'nonce too low': 'Transaction nonce error. Please try again.',
        'replacement transaction underpriced': 'A similar transaction is already pending with a higher gas price.',
        'already known': 'This transaction is already in the mempool.',
        'execution reverted': 'Transaction failed. Please check your inputs and try again.',
    };

    const lowerMessage = message.toLowerCase().trim();

    // Check for exact matches first
    for (const [key, value] of Object.entries(errorMappings)) {
        if (lowerMessage === key || lowerMessage.includes(key)) {
            return value;
        }
    }

    // Capitalize first letter and ensure it ends with punctuation
    let formatted = message.trim();
    if (formatted.length > 0) {
        formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
        if (!formatted.match(/[.!?]$/)) {
            formatted += '.';
        }
    }

    return formatted;
}


